import { TableContainer, Table, TableBody, Container, Stack, TablePagination } from '@mui/material';
import { tableContainer, table, tableHeadCellStyle } from './style';
import { TABLE_HEAD } from '../../../config';
import { TableRowItem } from '../table-row-item';
import { NewSite, SiteManagementProps } from 'src/features/sites/types/sites';
import { TableHeadCustom, MemoizedScrollbar, useTable, ContentLoadingError } from 'src/components';
import { SiteManagementRowsSkeleton } from '../../custom-skeletons';
import { useEffect, useState } from 'react';
import { SitesService } from 'src/services/sites';
import { useDebounce } from 'src/hooks/useDebounce';

export const DesktopSiteTable = ({
  data,
  dataCount,
  pageNum,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  customerId,
  skipId,
  search,
}: SiteManagementProps) => {
  const [sortedData, setSortedData] = useState<{ value: NewSite[] | null; loaded: boolean }>({
    value: [],
    loaded: false,
  });
  const { order, orderBy, selected, onSelectRow, onSort } = useTable();
  const [filteredDataCount, setFilteredDataCount] = useState<number>() || undefined;

  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const searchValue = useDebounce(search, 600) || '';

  useEffect(() => {
    if (search !== '') {
      setFilteredDataCount(sortedData.value?.length);
    } else {
      setFilteredDataCount(dataCount);
    }
  }, [search, setFilteredDataCount, sortedData.value?.length, dataCount]);

  useEffect(() => {
    if (!customerId) return;
    setSortedData((prevData) => ({ ...prevData, loaded: false }));
    SitesService.getAll({
      customerId: customerId,
      limit: rowsPerPage,
      skipTo: skipId,
      descending: order === 'asc' ? false : order === 'desc' ? true : false,
      sortBy: orderBy,
      search: searchValue,
    })
      .then((data: NewSite[]) => {
        if (data.length > 0) {
          setSortedData({ value: data, loaded: true });
        } else {
          setSortedData({ value: [], loaded: true });
        }
      })
      .catch((error) => {
        setSortedData({ value: null, loaded: true });
        console.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, order, skipId, searchValue, rowsPerPage]);

  useEffect(() => {
    setIsDisabled(filteredDataCount !== undefined && filteredDataCount < rowsPerPage);
  }, [filteredDataCount, rowsPerPage]);

  useEffect(() => {
    if (search) {
      onChangePage(null, 0);
    }
  }, [search, onChangePage]);

  return (
    <Container maxWidth={false} disableGutters data-sm="site-management-content">
      {data.loaded ? (
        <TablePagination
          nextIconButtonProps={
            isDisabled
              ? {
                  disabled: isDisabled,
                }
              : undefined
          }
          component={'div'}
          sx={{
            borderTop: 0,
            height: '42px',
            overflow: 'hidden',
            '.MuiTablePagination-selectLabel': { mt: 0, mb: 0 },
            '.MuiTablePagination-displayedRows': { mt: 0, mb: 0 },
          }}
          count={dataCount}
          page={pageNum}
          rowsPerPage={rowsPerPage}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      ) : null}
      <TableContainer sx={tableContainer}>
        <MemoizedScrollbar>
          <Table size="medium" sx={table} data-sm="site-management-table">
            <TableHeadCustom
              tableCellStyle={tableHeadCellStyle}
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              onSort={onSort}
              testId="table-headers"
            />

            <TableBody data-sm="table-site-management-content">
              {sortedData.loaded ? (
                sortedData.value?.map((row: NewSite) => (
                  <TableRowItem
                    key={row.id}
                    row={row}
                    selected={selected.includes(row.id)}
                    onSelectRow={() => onSelectRow(row.id)}
                  />
                ))
              ) : (
                <SiteManagementRowsSkeleton rowsNum={8} isMobile={false} />
              )}
            </TableBody>
          </Table>
        </MemoizedScrollbar>
      </TableContainer>
      {!sortedData.value ? (
        <Stack sx={{ width: '100%', alignItems: 'center' }}>
          <ContentLoadingError />
        </Stack>
      ) : null}
      {data.loaded ? (
        <TablePagination
          nextIconButtonProps={
            isDisabled
              ? {
                  disabled: isDisabled,
                }
              : undefined
          }
          component={'div'}
          sx={{
            borderTop: 0,
            height: '42px',
            overflow: 'hidden',
            '.MuiTablePagination-selectLabel': { mt: 0, mb: 0 },
            '.MuiTablePagination-displayedRows': { mt: 0, mb: 0 },
            mb: '32px',
          }}
          count={dataCount}
          page={pageNum}
          rowsPerPage={rowsPerPage}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      ) : null}
    </Container>
  );
};
